import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import {
  Typography, Checkbox, FormControlLabel, Link,
} from '@material-ui/core';

import { PreOnboardingParams } from '../../PreOnboardingPage';

import { Button, CoverageFields } from '@/components';
import { ENROLL_COVERED_EMPLOYEE_PATIENT } from '@/apollo/mutations';
import { EligibilityFormCustomization } from '@/pages/Onboarding/OnboardingFlowContext';
import { getPdtUid } from '@/utils/productHelpers';
import { getProgram } from '@/utils/posthog';
import { useUtmParams } from '@/hooks/useUtmParams';
import { useUserUid } from '@/hooks/useUserUid';
import { formatDateString, normalizeForEligibilityMatch } from '@/utils/stringUtils';
import { logError } from '@/utils/logError';

type Props = {
  formCustomization: EligibilityFormCustomization;
  onEligible: (careTeamUid: string, token: string, cortexId?: string) => void;
  onExistingUser: () => void;
  onIneligible: () => void;
  onError: () => void;
};

const CoverageCheckWithEmail = ({
  formCustomization, onEligible, onExistingUser, onIneligible, onError,
}: Props) => {
  const { t } = useTranslation();
  const { slug, product } = useParams<PreOnboardingParams>();
  const posthog = usePostHog();
  const { utmParams } = useUtmParams();
  const { setUserUid } = useUserUid();

  useEffect(() => {
    if (posthog) {
      posthog.capture('CoverageCheckStarted', { program: getProgram(product), org_slug: slug });
    }
  }, [posthog, product, slug]);

  const {
    handleSubmit, control, formState: { isValid },
  } = useFormContext();

  const [enrollCoveredEmployeePatient, { loading }] = useMutation(
    ENROLL_COVERED_EMPLOYEE_PATIENT,
  );

  const [submitError, setSubmitError] = useState(false);

  const onSubmit = useCallback(async (data: Record<string, string>) => {
    setSubmitError(false);

    try {
      const response = await enrollCoveredEmployeePatient({
        variables: {
          email: data.email,
          firstName: normalizeForEligibilityMatch(data.firstName),
          lastName: normalizeForEligibilityMatch(data.lastName),
          dateOfBirth: formatDateString(data.dateOfBirth),
          postalCode: formCustomization.showZipCode ? data.zipCode : undefined,
          employeeId: formCustomization.showEmployeeId ? data.employeeId : undefined,
          orgSlug: slug,
          pdtUid: getPdtUid(product),
          termsAndPrivacyAcknowledgedAt: new Date().toISOString(),
          posthogDistinctId: posthog.get_distinct_id(),
          ...utmParams,
        },
      });

      const result = response?.data?.enrollCoveredEmployeePatient;

      // Must resolve the person before ActivationFlowSubmitted carries their cortex_id.
      if (result?.eligible || result?.existingUser) {
        posthog.identify(result.distinctId);
      }

      posthog.capture('ActivationFlowSubmitted', {
        email: data.email,
        program: getProgram(product),
        ...(result?.cortexId ? { 'wellness.cortex_id': result.cortexId } : {}),
      });

      if (!result?.success) {
        onError();
        return;
      }

      // Unset means Cortex's flag check disagreed and silently fell back to the old always-verify path.
      const isUnset = (value: unknown) => value === null || value === undefined;

      // Same shape control fires (ConfirmDetails.tsx:291); only the two screens the schema names,
      // so anyone routed to check-your-email is left to ConfirmDetails, which fires it there.
      if (!isUnset(result.eligible) && !result.existingUser) {
        posthog.capture('CoverageCheckCompleted', {
          program: getProgram(product),
          coverage_check_status: result.eligible ? 'pass' : 'fail',
          ...(result.cortexId ? { 'wellness.cortex_id': result.cortexId } : {}),
        });
      }

      if (result.eligible) {
        if (!result.careTeamUid || !result.token) {
          onError();
          return;
        }
        // This path skips ConfirmDetails, which normally sets this for CreatePassword's own event.
        setUserUid(result.cortexId ?? undefined);
        // Renders CreatePassword directly on this same page - no navigation, so nothing needs
        // to survive a page change (the native app's in-app browser doesn't preserve storage).
        onEligible(result.careTeamUid, result.token, result.cortexId ?? undefined);
        return;
      }

      const wentThroughOldVerificationPath = isUnset(result.eligible) && isUnset(result.existingUser);

      if (result.existingUser || wentThroughOldVerificationPath) {
        onExistingUser();
        return;
      }

      onIneligible();
    } catch (e) {
      logError('[CoverageCheckWithEmail]', 'enrollment exception', e);
      posthog.capture('ActivationFlowSubmitted', { email: data.email, program: getProgram(product) });
      setSubmitError(true);
    }
  }, [enrollCoveredEmployeePatient, formCustomization, slug, product, posthog, utmParams, setUserUid, onEligible]);

  const subtitle = formCustomization.isGeneric
    ? 'coverage.sleepio.patient.confirm_details.generic_subtitle'
    : 'coverage.sleepio.patient.confirm_details.subtitle';
  const submitButtonText = formCustomization.isGeneric
    ? 'coverage.sleepio.patient.confirm_details.generic_submit_button'
    : 'coverage.sleepio.patient.confirm_details.submit_button';

  return (
    <>
      <Typography variant="h2" style={{ marginTop: 32, marginBottom: 8 }} color="textPrimary">
        {t('coverage.sleepio.patient.coverage_check_with_email.title')}
      </Typography>
      <Typography style={{ marginBottom: 32 }} color="textPrimary">
        {t(subtitle)}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CoverageFields formCustomization={formCustomization} showEmail />
        <Controller
          name="termsAndPrivacy"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControlLabel
              style={{ marginTop: 24, marginBottom: 8, alignItems: 'flex-start' }}
              label={(
                <Typography>
                  {`${t('coverage.i_agree_to_the')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.terms_of_use.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {t('footer.terms_of_use.label')}
                  </Link>
                  {` ${t('coverage.and')} `}
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={t('footer.privacy_notice.link')}
                    underline="always"
                    color="textPrimary"
                  >
                    {`${t('footer.privacy_notice.label')}.`}
                  </Link>
                </Typography>
              )}
              // checked binds the box to the form value, which outlives this step's remount.
              control={<Checkbox {...field} checked={!!field.value} />}
            />
          )}
        />
        {submitError && (
          <Typography color="error" style={{ marginTop: 8 }}>
            {t('coverage.errors.generic_error')}
          </Typography>
        )}
        <Button
          role="button"
          variant="contained"
          data-testid="submit-button"
          type="submit"
          fullWidth
          color="primary"
          disabled={!isValid}
          rounded
          loading={loading}
          style={{ marginTop: 24 }}
        >
          {t(submitButtonText)}
        </Button>
      </form>
    </>
  );
};

export default CoverageCheckWithEmail;
